import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Progress,
  Row,
  Table,
} from "reactstrap";
import { ClientDetailDto } from "src/store/clients/type";
import {
  getClientWebMail,
  resumeClientWebMail,
  suspendClientWebMail,
  teardownClientWebMail,
} from "src/store/client-webmail/saga";
import { ClientWebMailDto } from "src/store/client-webmail/type";

const ClientWebMailTab = ({
  active,
  client,
}: {
  active: string;
  client: ClientDetailDto;
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [data, setData] = useState<ClientWebMailDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [teardownOpen, setTeardownOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    dispatch(
      getClientWebMail({
        id: client.Id,
        payload: {
          onSuccess: (_m: any, r: ClientWebMailDto) => {
            setData(r);
            setLoading(false);
          },
          onError: () => setLoading(false),
        },
      })
    );
  }, [dispatch, client.Id]);

  useEffect(() => {
    if (active === "5") load();
  }, [active, load]);

  const doSuspend = () => {
    setBusy("suspend");
    dispatch(
      suspendClientWebMail({
        id: client.Id,
        payload: {
          onSuccess: () => {
            setBusy("");
            toast.success(t("WebMail.Suspended", "Hizmet askıya alındı."));
            load();
          },
          onError: (m: string) => {
            setBusy("");
            toast.error(m || t("WebMail.Error", "İşlem başarısız."));
          },
        },
      })
    );
  };

  const doResume = () => {
    setBusy("resume");
    dispatch(
      resumeClientWebMail({
        id: client.Id,
        payload: {
          onSuccess: () => {
            setBusy("");
            toast.success(t("WebMail.Resumed", "Hizmet yeniden aktif."));
            load();
          },
          onError: (m: string) => {
            setBusy("");
            toast.error(m || t("WebMail.Error", "İşlem başarısız."));
          },
        },
      })
    );
  };

  const doTeardown = () => {
    setBusy("teardown");
    dispatch(
      teardownClientWebMail({
        id: client.Id,
        body: { Confirm: confirmText },
        payload: {
          onSuccess: () => {
            setBusy("");
            setTeardownOpen(false);
            setConfirmText("");
            toast.success(t("WebMail.TearedDown", "Tüm web & e-posta ayarları kaldırıldı."));
            load();
          },
          onError: (m: string) => {
            setBusy("");
            toast.error(m || t("WebMail.Error", "İşlem başarısız."));
          },
        },
      })
    );
  };

  if (active !== "5") return null;

  const web = data?.web;
  const mail = data?.mail;
  const brevo = data?.brevo;
  const domain = data?.domain || null;

  const brevoBadge = () => {
    if (brevo?.authenticated || brevo?.status === "authenticated")
      return <Badge color="success">{t("WebMail.OutboundOk", "Gönderim açık")}</Badge>;
    if (brevo?.status === "added")
      return <Badge color="info">{t("WebMail.OutboundWait", "DNS bekleniyor")}</Badge>;
    return <Badge color="warning">{t("WebMail.OutboundPending", "Hazırlanıyor")}</Badge>;
  };

  return (
    <React.Fragment>
      {loading && !data ? (
        <div className="text-center py-4">{t("WebMail.Loading", "Yükleniyor…")}</div>
      ) : !domain ? (
        <Card>
          <CardBody className="text-center py-4 text-muted">
            {t("WebMail.NoDomain", "Bu müşteri henüz bir alan adı bağlamamış.")}
          </CardBody>
        </Card>
      ) : (
        <>
          {/* Üst aksiyon çubuğu */}
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2">
              <h5 className="mb-0">
                <code>{domain}</code>
              </h5>
              {data?.suspended ? (
                <Badge color="danger">{t("WebMail.StatusSuspended", "Askıya alındı")}</Badge>
              ) : (
                <Badge color="success">{t("WebMail.StatusActive", "Aktif")}</Badge>
              )}
            </div>
            <div className="d-flex gap-2">
              <Button color="light" size="sm" onClick={load}>
                <i className="bx bx-refresh me-1" />
                {t("WebMail.Refresh", "Yenile")}
              </Button>
              {data?.suspended ? (
                <Button color="success" size="sm" disabled={busy !== ""} onClick={doResume}>
                  {t("WebMail.Resume", "Geri Aç")}
                </Button>
              ) : (
                <Button color="warning" size="sm" disabled={busy !== ""} onClick={doSuspend}>
                  {t("WebMail.Suspend", "Askıya Al")}
                </Button>
              )}
              <Button color="danger" size="sm" outline onClick={() => setTeardownOpen(true)}>
                {t("WebMail.Teardown", "Tüm Ayarları Kaldır")}
              </Button>
            </div>
          </div>

          <Row>
            {/* Web sitesi */}
            <Col md={6} className="mb-3">
              <Card className="h-100">
                <CardBody>
                  <h6 className="mb-3">
                    <i className="bx bx-globe me-1" />
                    {t("WebMail.WebTitle", "Web Sitesi")}
                  </h6>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">{t("WebMail.Domain", "Alan adı")}</span>
                    <a href={web?.url} target="_blank" rel="noreferrer">
                      {domain}
                    </a>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">{t("WebMail.WebDns", "A kaydı (yayın)")}</span>
                    {web?.verified ? (
                      <Badge color="success">✓</Badge>
                    ) : (
                      <Badge color="warning">{t("WebMail.Pending", "Bekliyor")}</Badge>
                    )}
                  </div>
                  <div className="d-flex justify-content-between">
                    <span className="text-muted">{t("WebMail.Target", "Hedef IP")}</span>
                    <code>{web?.target}</code>
                  </div>
                </CardBody>
              </Card>
            </Col>

            {/* Giden e-posta */}
            <Col md={6} className="mb-3">
              <Card className="h-100">
                <CardBody>
                  <h6 className="mb-3">
                    <i className="bx bx-mail-send me-1" />
                    {t("WebMail.OutboundTitle", "Giden E-posta")}
                  </h6>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">{t("WebMail.Status", "Durum")}</span>
                    {brevoBadge()}
                  </div>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    {t(
                      "WebMail.OutboundHint",
                      "Giden mail kendi sunucumuzdan gönderiliyor. MX ve DKIM yayılınca gönderim otomatik açılır; ayrı doğrulama gerekmez."
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* E-posta hesapları */}
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">
                  <i className="bx bx-envelope me-1" />
                  {t("WebMail.MailTitle", "Posta Kutuları")}
                </h6>
                <div className="d-flex gap-2 align-items-center">
                  <Badge color={mail?.mxVerified ? "success" : "warning"}>
                    MX {mail?.mxVerified ? "✓" : "…"}
                  </Badge>
                  <Badge color={mail?.dkimVerified ? "success" : "warning"}>
                    DKIM {mail?.dkimVerified ? "✓" : "…"}
                  </Badge>
                  <Badge color="light" className="text-dark">
                    {t("WebMail.Boxes", "Kutu")}: {mail?.used ?? 0} / {mail?.limit ?? 0}
                  </Badge>
                  <Badge color="light" className="text-dark">
                    {t("WebMail.Storage", "Alan")}: {mail?.totalUsedMb ?? 0} MB
                  </Badge>
                </div>
              </div>

              {!mail?.inMailcow ? (
                <div className="text-muted">{t("WebMail.MailNotReady", "Mail altyapısı henüz kurulmamış.")}</div>
              ) : (mail?.mailboxes || []).length === 0 ? (
                <div className="text-muted">{t("WebMail.NoMailboxes", "Henüz posta kutusu yok.")}</div>
              ) : (
                <div className="table-responsive">
                  <Table className="align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>{t("WebMail.Address", "Adres")}</th>
                        <th>{t("WebMail.Name", "Ad")}</th>
                        <th>{t("WebMail.Active", "Aktif")}</th>
                        <th style={{ minWidth: 180 }}>{t("WebMail.Usage", "Kullanım")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(mail?.mailboxes || []).map((m) => (
                        <tr key={m.username}>
                          <td>{m.username}</td>
                          <td>{m.name || "—"}</td>
                          <td>
                            {m.active ? (
                              <Badge color="success">✓</Badge>
                            ) : (
                              <Badge color="secondary">—</Badge>
                            )}
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <Progress
                                value={m.usedPct}
                                style={{ height: 6, flex: 1, minWidth: 90 }}
                                color={m.usedPct > 85 ? "danger" : "primary"}
                              />
                              <small className="text-muted" style={{ whiteSpace: "nowrap" }}>
                                {m.usedMb} / {m.quotaMb} MB
                              </small>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </>
      )}

      {/* Teardown onay modalı */}
      <Modal isOpen={teardownOpen} toggle={() => setTeardownOpen(false)} centered>
        <ModalHeader toggle={() => setTeardownOpen(false)}>
          {t("WebMail.TeardownTitle", "Tüm Web & E-posta Ayarlarını Kaldır")}
        </ModalHeader>
        <ModalBody>
          <p className="text-danger mb-2">
            <b>{t("WebMail.TeardownWarn", "Bu işlem geri alınamaz.")}</b>
          </p>
          <p className="text-muted" style={{ fontSize: 13 }}>
            {t(
              "WebMail.TeardownDesc",
              "Posta kutuları (içindeki tüm e-postalar dahil), Mailcow alan adı ve Brevo kaydı kalıcı olarak silinir; web sitesi bağlantısı kaldırılır. Katalog/ürün verileri korunur."
            )}
          </p>
          <p className="mb-1" style={{ fontSize: 13 }}>
            {t("WebMail.TeardownConfirmLabel", "Onaylamak için alan adını yazın:")}{" "}
            <code>{domain}</code>
          </p>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={domain || ""}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setTeardownOpen(false)}>
            {t("WebMail.Cancel", "Vazgeç")}
          </Button>
          <Button
            color="danger"
            disabled={busy === "teardown" || confirmText.trim().toLowerCase() !== (domain || "").toLowerCase()}
            onClick={doTeardown}
          >
            {t("WebMail.ConfirmTeardown", "Kalıcı Olarak Kaldır")}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default ClientWebMailTab;
